'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PluggyConnectInner, PluggyConnectModal, type PluggyConnectSession } from './PluggyConnect';
import { ImportProgress, type ImportProgressProps } from './ImportProgress';
import { useFinance } from '@/hooks/useFinance';

export interface ConnectFlowState {
  open: boolean;
  itemId?: string;
  connectorName?: string;
  connectorImage?: string | null;
  reason?: 'new' | 'update' | 'reauth' | 'renew';
}

export interface ImportState {
  /** True if we should render the stepper instead of the Pluggy widget. */
  active: boolean;
  steps: ImportProgressProps['steps'];
  alert?: ImportProgressProps['alert'];
}

interface Props {
  state: ConnectFlowState;
  importState: ImportState;
  onOpenChange: (open: boolean) => void;
  onSuccess: (pluggyItemId: string) => void;
  /** The attempt was abandoned: preflight dismissed or the widget closed with
   * no item handed over. NOT fired on the launch handoff (the preflight closes
   * itself to make room for the widget) nor on the import stepper's close. */
  onAbort?: () => void;
  /** The preflight handed off to the Pluggy widget — the OAuth hop can follow
   * at any moment. The launch handoff, unlike the dialog opening, is the real
   * 'a return may come' signal. */
  onLaunched?: () => void;
}

const CONNECT_COPY: Record<NonNullable<ConnectFlowState['reason']>, { title: string; description: string }> = {
  new: {
    title: 'Conectar conta',
    description: 'Autorize uma instituição pelo Open Finance para trazer saldos, transações e investimentos.',
  },
  update: {
    title: 'Atualizar conexão',
    description: 'Continue a autorização solicitada pela instituição para manter seus dados em dia.',
  },
  reauth: {
    title: 'Reconectar instituição',
    description: 'Entre novamente na instituição para restaurar a sincronização dos seus dados.',
  },
  renew: {
    title: 'Renovar consentimento',
    description: 'Renove o consentimento Open Finance para manter esta instituição conectada.',
  },
};

export function ConnectionUpdateDialog({ state, importState, onOpenChange, onSuccess, onAbort, onLaunched }: Props) {
  const { fetchConnectorHealth } = useFinance();
  const [activePluggySession, setActivePluggySession] = useState<PluggyConnectSession | null>(null);
  const [allowedConnectorIds, setAllowedConnectorIds] = useState<number[] | undefined>(undefined);
  const [connectorFilterReady, setConnectorFilterReady] = useState(false);

  const handleLaunch = useCallback((session: PluggyConnectSession) => {
    setActivePluggySession(session);
    onLaunched?.();
    onOpenChange(false);
  }, [onLaunched, onOpenChange]);

  const handlePluggyClose = useCallback(() => {
    setActivePluggySession(null);
    // Closed without a handover = the attempt is over. On the success path the
    // widget hands the item over first (handlePluggySuccess), so an abort after
    // it repeats clearing the parent already did - a no-op there.
    onAbort?.();
  }, [onAbort]);

  const handlePluggySuccess = useCallback((pluggyItemId: string) => {
    setActivePluggySession(null);
    onSuccess(pluggyItemId);
  }, [onSuccess]);

  // The preflight dialog's own dismissals (Escape, overlay, its Fechar) are
  // cancellations. handleLaunch bypasses this on purpose: it calls onOpenChange
  // directly, so the handoff to the widget never reads as an abort.
  const handlePreflightOpenChange = useCallback((open: boolean) => {
    onOpenChange(open);
    if (!open) onAbort?.();
  }, [onAbort, onOpenChange]);
  const handlePreflightClose = useCallback(() => handlePreflightOpenChange(false), [handlePreflightOpenChange]);

  const pluggyModal = activePluggySession ? (
    <PluggyConnectModal
      session={activePluggySession}
      onSuccess={handlePluggySuccess}
      onClose={handlePluggyClose}
    />
  ) : null;

  const copy = CONNECT_COPY[state.reason ?? 'new'];
  const isNewConnection = (state.reason ?? 'new') === 'new';

  useEffect(() => {
    if (!state.open || !isNewConnection) return;
    let cancelled = false;
    setAllowedConnectorIds(undefined);
    setConnectorFilterReady(false);

    void fetchConnectorHealth()
      .then((connectors) => {
        if (cancelled) return;
        setAllowedConnectorIds(
          connectors.length > 0
            ? connectors.filter((connector) => connector.selectable).map((connector) => connector.connectorId)
            : undefined,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[ConnectionUpdateDialog] Failed to prepare connector filter:', err);
        setAllowedConnectorIds(undefined);
      })
      .finally(() => {
        if (!cancelled) setConnectorFilterReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchConnectorHealth, isNewConnection, state.open]);

  useEffect(() => {
    if (state.open) return;
    setAllowedConnectorIds(undefined);
    setConnectorFilterReady(false);
  }, [state.open]);

  if (importState.active) {
    return (
      <>
        <Dialog open={state.open} onOpenChange={onOpenChange}>
          {/* No overflow-hidden here: it would undo the primitive's own
              overflow-y-auto and re-trap the stepper under zoom. */}
          <DialogContent className="max-w-md p-0">
            <ImportProgress
              variant="vertical"
              steps={importState.steps}
              alert={importState.alert}
              connectorName={state.connectorName}
              connectorImage={state.connectorImage}
              onClose={() => onOpenChange(false)}
            />
          </DialogContent>
        </Dialog>
        {pluggyModal}
      </>
    );
  }

  if (!state.open) return pluggyModal;

  return (
    <>
      <Dialog open={state.open} onOpenChange={handlePreflightOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <div className="p-4 sm:p-6">
            <PluggyConnectInner
              key={`${state.itemId ?? 'new'}-${state.open ? 'on' : 'off'}`}
              itemId={state.itemId}
              allowedConnectorIds={isNewConnection ? allowedConnectorIds : undefined}
              launchDisabled={isNewConnection && !connectorFilterReady}
              onLaunch={handleLaunch}
              onClose={handlePreflightClose}
            />
          </div>
        </DialogContent>
      </Dialog>
      {pluggyModal}
    </>
  );
}
